import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const MARK = '__leaveGuard';
const marked = () => !!(window.history.state as Record<string, unknown> | null)?.[MARK];
const pushMark = () => window.history.pushState({ ...((window.history.state as object | null) ?? {}), [MARK]: true }, '');

/**
 * Asks before leaving unsaved work inside the app (BrowserRouter has no blocker): a click on an in-app link to another
 * page is held until `ask()` says yes; with `back`, the browser's Back is held too (one extra history entry stands in
 * front of the page while guarded, so Back lands on it and the question is asked there). Closing or reloading the tab
 * is `useLeaveGuard`'s job.
 */
export function useNavigationGuard(guard: { links: boolean; back: boolean }, ask: () => Promise<boolean>) {
  const navigate = useNavigate();
  const askRef = useRef(ask);
  useLayoutEffect(() => {
    askRef.current = ask;
  });
  const { links, back } = guard;

  useEffect(() => {
    if (!links) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || (url.pathname === window.location.pathname && url.search === window.location.search)) return;
      e.preventDefault();
      e.stopPropagation();
      void askRef.current().then((ok) => ok && navigate(`${url.pathname}${url.search}${url.hash}`));
    };
    // capture: before the router's own link handler
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [links, navigate]);

  useEffect(() => {
    if (!back) return;
    if (!marked()) pushMark();
    let leaving = false;
    const onPop = () => {
      if (leaving || marked()) return;
      // the stand-in entry was popped: still on this page
      void askRef.current().then((ok) => {
        if (ok) {
          leaving = true;
          window.history.back();
        } else pushMark();
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [back]);
}
